import { parseApiDateOrDateTime } from './dateParsing'
import { retriveLienFromPractice } from './script'

/**
 * Canonical insurance/attorney notice list for a practice.
 * Prefers practice.intent_notice_documents (API); falls back to intent lien nested list.
 */
export function getPracticeIntentNoticeDocuments(practice) {
    if (!practice) {
        return []
    }
    if (Array.isArray(practice.intent_notice_documents) && practice.intent_notice_documents.length > 0) {
        return practice.intent_notice_documents
    }
    const intentLien = retriveLienFromPractice(practice.liens, 'intent')
    if (intentLien && Array.isArray(intentLien.intent_notice_documents)) {
        return intentLien.intent_notice_documents
    }
    return []
}

/**
 * Canonical satisfaction-letter document list for a practice.
 * Prefers practice.satisfaction_letter_documents (API); falls back to satisfy lien nested list.
 */
export function getPracticeSatisfactionLetterDocuments(practice) {
    if (!practice) {
        return []
    }
    if (Array.isArray(practice.satisfaction_letter_documents) && practice.satisfaction_letter_documents.length > 0) {
        return practice.satisfaction_letter_documents
    }
    const satisfyLien = retriveLienFromPractice(practice.liens, 'satisfy')
    if (satisfyLien && Array.isArray(satisfyLien.satisfaction_letter_documents)) {
        return satisfyLien.satisfaction_letter_documents
    }
    return []
}

function satisfactionLetterIdentity(doc, index) {
    const id = doc?.id ?? doc?.document?.id
    if (id != null && `${id}`.trim() !== '') {
        return `id:${id}`
    }
    return `idx:${index}:${doc?.name ?? ''}:${doc?.created_at ?? ''}`
}

function collectSatisfactionLetterDocuments(practice) {
    const docs = []
    const seen = new Set()
    const push = (list) => {
        if (!Array.isArray(list)) {
            return
        }
        for (const doc of list) {
            if (!doc) {
                continue
            }
            const key = satisfactionLetterIdentity(doc, docs.length)
            if (seen.has(key)) {
                continue
            }
            seen.add(key)
            docs.push(doc)
        }
    }

    push(practice.satisfaction_letter_documents)
    const liens = Array.isArray(practice.liens) ? practice.liens : []
    for (const lien of liens) {
        if (`${lien?.type || ''}`.toLowerCase() !== 'satisfy') {
            continue
        }
        push(lien.satisfaction_letter_documents)
    }
    return docs
}

function satisfactionLetterParty(doc) {
    const name = `${doc?.name || ''}`.toLowerCase()
    if (name.includes('attorney') || name.includes('atty')) {
        return 'attorney'
    }
    if (name.includes('patient')) {
        return 'patient'
    }
    return null
}

function satisfactionLetterCreatedTime(doc) {
    const parsed = parseApiDateOrDateTime(doc?.created_at)
    const time = parsed?.getTime()
    return Number.isFinite(time) ? time : -Infinity
}

function isNewerSatisfactionLetter(candidate, current) {
    const candidateTime = satisfactionLetterCreatedTime(candidate)
    const currentTime = satisfactionLetterCreatedTime(current)
    if (candidateTime !== currentTime) {
        return candidateTime > currentTime
    }
    const candidateId = Number(candidate?.id)
    const currentId = Number(current?.id)
    return (Number.isFinite(candidateId) ? candidateId : -Infinity) >
        (Number.isFinite(currentId) ? currentId : -Infinity)
}

/**
 * Letters shown in the lien drawer.
 * Patient and attorney copies collapse to the latest created letter in each group.
 * Other names are unchanged.
 */
export function getLatestPracticeSatisfactionLetters(practice) {
    if (!practice) {
        return []
    }

    const docs = collectSatisfactionLetterDocuments(practice)
    let latestPatient = null
    let latestAttorney = null
    for (const doc of docs) {
        const party = satisfactionLetterParty(doc)
        if (party === 'patient' && (!latestPatient || isNewerSatisfactionLetter(doc, latestPatient))) {
            latestPatient = doc
        } else if (party === 'attorney' && (!latestAttorney || isNewerSatisfactionLetter(doc, latestAttorney))) {
            latestAttorney = doc
        }
    }

    return docs.filter((doc) => {
        const party = satisfactionLetterParty(doc)
        if (party === 'patient') {
            return doc === latestPatient
        }
        if (party === 'attorney') {
            return doc === latestAttorney
        }
        return true
    })
}

export function practiceHasLienDrawerContent(practice) {
    if (!practice) {
        return false
    }
    if (Array.isArray(practice.liens) && practice.liens.length > 0) {
        return true
    }
    return getPracticeIntentNoticeDocuments(practice).length > 0 ||
        getPracticeSatisfactionLetterDocuments(practice).length > 0
}
